import type { ReactNode } from "react";

// 앱 도메인 16px 아이콘 모음이다. c6 public glyph에 없는 아이콘은 여기에서 currentColor 기반 SVG 로
// 유지한다. 페이지 액션 버튼은 라인(`IconSvg`)을 쓴다. GNB 항목은 c6 글리프와 같은 fill 기반
// (`FilledIconSvg`)을 쓴다. 한 메뉴 안에 선 아이콘과 면 아이콘이 섞이면 모양 차이가 눈에 띈다.
function IconSvg({ children }: { children: ReactNode }) {
  return (
    <svg
      aria-hidden="true"
      width="1em"
      height="1em"
      viewBox="0 0 16 16"
      fill="none"
      stroke="currentColor"
      strokeWidth="1.4"
      strokeLinecap="round"
      strokeLinejoin="round"
    >
      {children}
    </svg>
  );
}

// c6 글리프와 같은 규격이다(16 그리드 · 1em · fill=currentColor). GNB 항목은 c6 아이콘과 나란히
// 놓인다. 그래서 stroke 기반 `IconSvg` 대신 이 셸을 쓴다.
function FilledIconSvg({ children }: { children: ReactNode }) {
  return (
    <svg aria-hidden="true" width="1em" height="1em" viewBox="0 0 16 16" fill="currentColor">
      {children}
    </svg>
  );
}

// 막대 차트 아이콘이다(통계 메뉴). c6 에는 꺾은선(`GraphLineIcon`)만 있고 막대 글리프가 없다.
//
// 칠해진 영역을 y 2.5~13.5 로 두어 16 그리드 가운데(8)에 맞춘다. GNB 는 아이콘 상자를 라벨과
// 가운데 정렬한다. 그래서 칠해진 영역이 한쪽으로 치우치면 그 항목만 아래로 처져 보인다.
// 축선을 상자 맨 아래(y 13~14.5)에 둔 그림은 중심이 0.59 였다. c6 글리프들의 정규화 중심은
// 0.44~0.52 다. 막대는 축선 위에 바로 세워 막대와 축선 사이에 틈이 없게 한다.
export function BarChartIcon() {
  return (
    <FilledIconSvg>
      <path d="M1 12h14v1.5H1z" />
      <path d="M2.5 8h2.5v4H2.5zM6.75 2.5h2.5V12h-2.5zM11 5.5h2.5V12H11z" />
    </FilledIconSvg>
  );
}

export function RefreshIcon() {
  return (
    <IconSvg>
      <path d="M13 8a5 5 0 1 1-1.46-3.54" />
      <path d="M13 2.5V5h-2.5" />
    </IconSvg>
  );
}

export function ArchiveIcon() {
  return (
    <IconSvg>
      <path d="M2.5 4.5h11v2h-11z" />
      <path d="M3.5 6.5v6h9v-6" />
      <path d="M6.5 9h3" />
    </IconSvg>
  );
}

export function UnarchiveIcon() {
  return (
    <IconSvg>
      <path d="M2.5 4.5h11v2h-11z" />
      <path d="M3.5 6.5v6h9v-6" />
      <path d="M8 11.5V8M6.25 9.5 8 7.75 9.75 9.5" />
    </IconSvg>
  );
}

export function ChevronLeftIcon() {
  return (
    <IconSvg>
      <path d="M10 4 6 8l4 4" />
    </IconSvg>
  );
}

export function ChevronRightIcon() {
  return (
    <IconSvg>
      <path d="M6 4l4 4-4 4" />
    </IconSvg>
  );
}

export function FolderOutlineIcon() {
  return (
    <IconSvg>
      <path d="M2 4.5a1 1 0 0 1 1-1h3l1.3 1.5H13a1 1 0 0 1 1 1V12a1 1 0 0 1-1 1H3a1 1 0 0 1-1-1z" />
    </IconSvg>
  );
}

// 더하기 아이콘이다(새 폴더 만들기). c6 public glyph 에 없다.
export function PlusIcon() {
  return (
    <IconSvg>
      <path d="M8 3.5v9M3.5 8h9" />
    </IconSvg>
  );
}

// 채운 폴더 아이콘이다(데이터셋 폴더 트리 레인). 파일 브라우저의 물리 디렉터리 트리는 선
// 아이콘(`FolderOutlineIcon`)을 쓴다. 두 트리는 다른 개념이고 한 화면에 함께 표시되지 않는다.
// 이 아이콘은 앱 화면의 기본인 면 표현을 따른다.
export function FolderIcon() {
  return (
    <FilledIconSvg>
      <path d="M2 4.5a1 1 0 0 1 1-1h3l1.3 1.5H13a1 1 0 0 1 1 1V12a1 1 0 0 1-1 1H3a1 1 0 0 1-1-1z" />
    </FilledIconSvg>
  );
}

export function VideoCameraIcon() {
  return (
    <IconSvg>
      <rect x="2.5" y="4.5" width="7.5" height="7" rx="1" />
      <path d="M10 7.2 13.5 5v6L10 8.8z" />
    </IconSvg>
  );
}

export function FileIcon() {
  return (
    <IconSvg>
      <path d="M4 2.5h5l3 3V13a.5.5 0 0 1-.5.5h-7A.5.5 0 0 1 4 13z" />
      <path d="M9 2.5V5.5h3" />
    </IconSvg>
  );
}

export function DownloadIcon() {
  return (
    <IconSvg>
      <path d="M8 2.5v7m0 0 2.5-2.5M8 9.5 5.5 7M3 12.5h10" />
    </IconSvg>
  );
}

// 업로드 아이콘이다. DownloadIcon 의 화살표를 뒤집어 짝을 맞춘다.
export function UploadIcon() {
  return (
    <IconSvg>
      <path d="M8 9.5v-7m0 0L5.5 5M8 2.5 10.5 5M3 12.5h10" />
    </IconSvg>
  );
}
